import { FunctionComponent, useState, useCallback, useEffect, useMemo, ChangeEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { makeStyles } from '@griffel/react'
import { SearchRegular, DismissRegular } from '@fluentui/react-icons'
import { Button } from '@fluentui/react-button'
import { Field } from '@fluentui/react-field'
import { Input, InputOnChangeData } from '@fluentui/react-input'
import { tokens } from '@fluentui/tokens'
import { IFilter } from '@knowmax/genericlist-core'
import { fieldIdForOperator } from '@knowmax/genericlist-core'
import { useGenericListSettings, getParamId } from '@knowmax/genericlist-core'
import { LANGUAGE_PLACEHOLDERSEARCH } from '../../types/language'
import { OperatorSelector } from '.'

const useClasses = makeStyles({
  rootvertical: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS
  },
  buttoncontainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
    justifyContent: 'end',
    columnGap: tokens.spacingHorizontalXS
  }
})

/** Renders a single text input filter. Serializes and deserializes in query params of url. */
export const FilterText: FunctionComponent<{ filter: IFilter, listId?: string }> = observer(({ filter, listId }) => {
  const classes = useClasses()
  const [value, setValue] = useState('') // used for full text filters only
  const settings = useGenericListSettings()

  const filterparamid = useMemo(() => getParamId(filter.id, listId), [filter.id, listId])

  useEffect(() => {
    setValue(settings.params.get(filterparamid) ?? '')
  }, [settings.params, filterparamid])

  const execute = useCallback((query: string) => {
    const params = new URLSearchParams(settings.params)
    params.delete(filterparamid)

    if (query !== '') {
      params.set(filterparamid, query)
    } else {
      params.delete(fieldIdForOperator(filterparamid))
    }

    settings.serialize(params)
  }, [settings.params, filterparamid])

  const changevalue = useCallback((ev: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    setValue(data.value ?? '')
  }, [])

  const clearvalue = useCallback(() => {
    setValue('')
    execute('')
  }, [execute])

  const onenter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      execute(value)
    }
  }, [execute, value])

  const onexecute = useCallback(() => {
    execute(value)
  }, [execute, value])

  const allowoperatorselection = useMemo(() => filter.operators && filter.operators.length > 0, [filter.operators])

  return (
    <div className={classes.rootvertical}>
      <Field label={filter.name}>
        <Input
          autoComplete='off'
          data-1p-ignore
          placeholder={filter.placeholderText ?? settings.onTranslate(LANGUAGE_PLACEHOLDERSEARCH)}
          value={value}
          spellCheck={false}
          onChange={changevalue}
          onKeyUp={onenter}
        />
      </Field>

      <div className={classes.buttoncontainer} >
        {allowoperatorselection &&
          <OperatorSelector filter={filter} listId={listId} />}
        <Button
          onClick={clearvalue}
          disabled={!value}
          appearance='transparent'
          icon={<DismissRegular />}
        />
        <Button
          onClick={onexecute}
          disabled={!value}
          appearance='transparent'
          icon={<SearchRegular />}
        />
      </div>
    </div>
  )
})